import { parse, serialize } from "tinyduration";
import { divide, multiply, round } from "mathjs";
import moment from "moment";
import { parse_flight_offers_date } from "@liveflight/utilities/dates";

export const parse_flight_offers_duration = (duration) => parse(duration);

export const encode_flight_offers_duration = (duration) => serialize(duration);

export const duration_to_timestamp = (duration) => {
  const { days = 0, hours = 0, minutes = 0 } = duration || {};
  return days * 86400 + hours * 3600 + minutes * 60;
};
export const get_day_delta = (departure, arrival) => {
  const date_departure = parse_flight_offers_date(departure).date;
  const date_arrival = parse_flight_offers_date(arrival).date;
  const delta = date_arrival.getTime() - date_departure.getTime();
  return round(divide(delta, multiply(1000, 3600, 24)), 0);
};

export const get_itinerary_delta = (itinerary = {}) => {
  const { segments = [] } = itinerary;
  if (segments.length <= 0) {
    return 0;
  }
  try {
    return get_day_delta(
      segments[0].departure.at,
      segments[segments.length - 1].arrival.at,
    );
  } catch {
    return 0;
  }
};
export const calculate_duration = ({ start, end, encoded = true }) => {
  const diff = {
    days: moment(end).diff(start, "days"),
    hours: moment(end).diff(start, "hours") % 24,
    minutes: moment(end).diff(start, "minutes") % 60,
  };
  if (encoded) {
    return encode_flight_offers_duration(diff);
  }
  return (
    (diff.days || 0) * 86400 +
    (diff.hours || 0) * 3600 +
    (diff.minutes || 0) * 60
  );
};

export const calculate_stop_duration = (segment, next_segment) => {
  if (!segment || !next_segment) {
    return "";
  }
  try {
    return calculate_duration({
      start: segment.arrival.at,
      end: next_segment.departure.at,
    });
  } catch {
    return "";
  }
};

export const calculate_itinerary_duration = (itinerary = {}) => {
  const duration = parse_flight_offers_duration(itinerary.duration);
  const timestamp = duration_to_timestamp(duration);
  if (timestamp > 0) {
    return timestamp;
  }
  // Sanity check: NDC sometimes return 0sec durations which is impossible so for those do a double check
  // Itinerary time is calculated from first departure to last arrival with stops included
  try {
    const { segments = [] } = itinerary;
    const first_departure = segments[0].departure.at;
    const last_arrival = segments[segments.length - 1].arrival.at;
    return calculate_duration({
      start: first_departure,
      end: last_arrival,
      encoded: false,
    });
  } catch {
    return timestamp;
  }
};

export const flight_offer_total_duration = (flight_offer, encoded = true) => {
  if (flight_offer.itineraries.length === 1) {
    return flight_offer.itineraries[0].duration;
  }

  const total = flight_offer.itineraries.reduce(
    (tot, itinerary) => tot + calculate_itinerary_duration(itinerary),
    0,
  );

  if (encoded) {
    return encode_flight_offers_duration({
      days: Math.floor(total / 86400),
      hours: Math.floor(total / 3600) % 24,
      minutes: Math.floor(total / 60) % 60,
    });
  }
  return total;
};

export const flight_offer_average_duration = (flight_offer, encoded = true) => {
  const total =
    flight_offer.itineraries.reduce(
      (tot, itinerary) => tot + calculate_itinerary_duration(itinerary),
      0,
    ) / flight_offer.itineraries.length;

  if (encoded) {
    return encode_flight_offers_duration({
      days: Math.floor(total / 86400),
      hours: Math.floor(total / 3600) % 24,
      minutes: Math.floor(total / 60) % 60,
    });
  }
  return total;
};
